import { Sheet } from '@startuptools/ui/Sheet';
import { styled } from '@mui/joy/styles';
import { Table } from '@startuptools/ui/Table';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { PieChart } from '@startuptools/ui/PieChart';
import { Typography } from '@startuptools/ui/Typography';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { BlurOverlay } from '@startuptools/ui/BlurOverlay';
import { Grid } from '@startuptools/ui/Grid';
import { Link } from '@startuptools/ui/Link';
import { CompanyCard } from '@startuptools/business-ui/company/CompanyCard';
import { useDashboardSuspenseQuery } from './dashboard.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Trans, useLingui } from '@lingui/react';
import React from 'react';
import { GqlFileSignatoryStatus, GqlDocSystemTagKind } from '../../../graphql/base-types.graphql';
import { DocumentTable } from './components/DocumentTable';
import { MeetingTable } from './components/MeetingTable';
import { WarrantProgramTable } from './components/WarrantProgramTable';
import { InvestmentTable } from './components/InvestmentsTable';
import { ValuationChart } from './components/ValuationChart';
import { ButtonGroup } from '@startuptools/ui/ButtonGroup';
import { Alert } from '@startuptools/ui/Alert';
import { useNavigate } from '../../../components/Navigate';
import { Link as RouterLink } from 'react-router-dom';

const Tr = styled(RouterLink)({ display: 'table-row' });

const Item = styled(Sheet)(({ theme }) => {
  return {
    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.level1 : '#fff',
    ...theme.typography['body-sm'],
    padding: theme.spacing(1),
    borderRadius: 4,
    color: theme.vars.palette.text.secondary,
    overflowX: 'scroll',
    '::-webkit-scrollbar': {
      display: 'none',
    },
  };
});

export const Dashboard = () => {
  const { link } = useNavigate();
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [_, startTransition] = React.useTransition();
  const [documentStatus, setDocumentStatus] = React.useState({
    [GqlFileSignatoryStatus.Pending]: true,
    [GqlFileSignatoryStatus.Ongoing]: true,
    [GqlFileSignatoryStatus.Closed]: true,
  });

  const { data } = useDashboardSuspenseQuery({
    variables: {
      companyId,
      fileFilter: {
        parentId: null,
        kinds: [GqlDocSystemTagKind.Other, GqlDocSystemTagKind.EmploymentAgreement, GqlDocSystemTagKind.OneWayNda],
        includeIndirectChildren: true,
        status: Object.entries(documentStatus)
          .filter(([_, value]) => value)
          .map(([key]) => key) as GqlFileSignatoryStatus[],
      },
    },
    fetchPolicy: 'no-cache',
  });

  const hasCreatedContent = Boolean(
    data.meetings.models.length || data.warrantPrograms.models.length || data.investments.length,
  );

  return (
    <Stack gap={2}>
      {!hasCreatedContent && (
        <Item variant="outlined">
          <Stack direction="column" gap={2} m={2}>
            <Typography level="h2">
              <Trans id="Welcome to Bolago!" />
            </Typography>
            <Typography level="body-sm">
              <Trans
                id="This overview shows all company information we have synced from Bolagsverket. Do you want us to show how the platform works? Click <link>here</link> to book a demo."
                components={{
                  link: (
                    <Link
                      sx={{ fontWeight: 'bold' }}
                      href="https://meetings-eu1.hubspot.com/lhemlin/linus-mote-clone"
                      target="_blank"
                    />
                  ),
                }}
              />
            </Typography>
            <Typography level="h4">
              <Trans id="Get started" />
            </Typography>

            <ButtonGroup size="lg">
              <Button variant="soft" component={RouterLink} to={link('/share-register/transactions')}>
                <Trans id="Create your share register" />
              </Button>
              <Button variant="soft" component={RouterLink} to={link('/stock-options/warrant-programs')}>
                <Trans id="Create an option program" />
              </Button>
              <Button variant="soft" component={RouterLink} to={link('/board-portal/board-meetings')}>
                <Trans id="Hold a board meeting" />
              </Button>
            </ButtonGroup>
          </Stack>
        </Item>
      )}
      <Stack direction="row" gap={2}>
        <Item variant="outlined" sx={{ flex: 1 }}>
          <CompanyCard company={data.company} />
        </Item>
        <Item variant="outlined" sx={{ flex: 1 }}>
          <Typography level="body-lg">
            <Trans id="Bolagsverket cases" />
          </Typography>

          <Table
            hoverRow
            sx={{ minWidth: '420px', '& tr': { cursor: 'pointer' }, '& thead th:nth-child(1)': { width: '40%' } }}
          >
            <thead>
              <tr>
                <th>
                  <Trans id="Name" />
                </th>
                <th>
                  <Trans id="Sent" />
                </th>
                <th>
                  <Trans id="Registered" />
                </th>
                <th>
                  <Trans id="Status" />
                </th>
              </tr>
            </thead>
            <tbody>
              {data.snrCases.models.map(c => (
                <Tr key={c.id} to={link('/about/cases')}>
                  <td>{c.subjects.map(s => s.value).join(', ')}</td>
                  <td>
                    <DateFormat locale={i18n.locale}>{c.arrivalDate}</DateFormat>
                  </td>
                  <td>
                    <DateFormat locale={i18n.locale}>{c.registrationDate}</DateFormat>
                  </td>
                  <td>
                    {c.status.find(s => s.kod === 'REG') ? <Trans id="Registered" /> : <Trans id="Being processed" />}
                  </td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Item>
      </Stack>

      <Stack direction="row" gap={2}>
        <Item variant="outlined" sx={{ flex: 1 }}>
          <Typography level="body-lg">
            <Trans id="Valuation" />
          </Typography>

          <ValuationChart
            currency={data.snrShareCapitalChanges[0]?.currency}
            data={data.snrShareCapitalChanges.map(snr => ({
              registrationDate: snr.registrationDate,
              valuation: Number(snr.shareValueSplitAdjusted) * Number(snr.sharesTotalSplitAdjusted),
              sharePrice: Number(snr.shareValueSplitAdjusted),
            }))}
          />
        </Item>
        <Item variant="outlined" sx={{ flex: 1 }}>
          <Typography level="body-lg">
            <Trans id="Shareholders" />
          </Typography>
          {!data.shareholders.length && (
            <Alert>
              <Trans id="You haven't added your share register yet - when you have, you'll see your top shareholders here." />
            </Alert>
          )}

          <BlurOverlay show={!data.shareholders.length}>
            <PieChart
              data={
                data.shareholders.length
                  ? [
                      ...data.shareholders.map(s => ({
                        value: s.shareState?.numberOfShares ?? 0,
                        name: s.identity?.name ?? 'Unknown',
                      })),
                      {
                        value:
                          data.company.shareRegisterShareCount -
                          data.shareholders.reduce((acc, curr) => acc + (curr?.shareState?.numberOfShares ?? 0), 0),
                        name: 'Other shareholders',
                      },
                    ]
                  : [
                      { value: 100, name: 'Erlich Bachman' },
                      { value: 50, name: 'Monica Hall' },
                      { value: 50, name: 'Nelson Bighetti' },
                      { value: 30, name: 'Other shareholders' },
                    ]
              }
            />
          </BlurOverlay>
        </Item>
      </Stack>

      {hasCreatedContent && (
        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <Item variant="outlined">
              <DocumentTable
                documents={data.file.children.models}
                toggleDocumentStatus={(status, value) =>
                  startTransition(() => setDocumentStatus(state => ({ ...state, [status]: value })))
                }
                toggleAll={() => {
                  return startTransition(() => {
                    return setDocumentStatus(state => {
                      const show = !Object.values(state).some(Boolean);
                      return {
                        [GqlFileSignatoryStatus.Pending]: show,
                        [GqlFileSignatoryStatus.Ongoing]: show,
                        [GqlFileSignatoryStatus.Closed]: show,
                      };
                    });
                  });
                }}
                show={documentStatus}
              />
            </Item>
          </Grid>
          <Grid xs={12} md={6}>
            <Item variant="outlined">
              <MeetingTable meetings={data.meetings.models} />
            </Item>
          </Grid>
          <Grid xs={12} md={6}>
            <Item variant="outlined">
              <WarrantProgramTable warrantPrograms={data.warrantPrograms.models} />
            </Item>
          </Grid>
          <Grid xs={12} md={6}>
            <Item variant="outlined">
              <InvestmentTable investments={data.investments} />
            </Item>
          </Grid>
        </Grid>
      )}
    </Stack>
  );
};
